export const MONTHS_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
export const MONTHS_SHORT = MONTHS_LONG.map(month => month.slice(0, 3));

const pad = (value) => String(value).padStart(2, '0');

export const toDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

// A pricing band is stored as [fromDate, toDate] and read back with an
// inclusive end (fromDate <= date <= toDate). Both places that write a band
// derive toDate from an instant that marks the *start* of the final day — the
// calendar hands back startOfDay, the distributor defaults add whole days to
// releaseDate — so the last day is left uncovered and the next band opens a
// full day later. Closing the band a millisecond before the following day
// begins makes consecutive bands contiguous without overlapping them.
export const inclusiveEnd = (value) => {
    const date = toDate(value);
    if (!date) return null;

    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return new Date(nextDay.getTime() - 1);
};

// Token formatter covering the patterns the app actually uses. Longest tokens are
// listed first so the alternation matches MMMM before MMM before MM.
export const formatDateBy = (value, pattern = 'dd MMM yyyy') => {
    const date = toDate(value);
    if (!date) return '';

    const tokens = {
        yyyy: String(date.getFullYear()),
        yy: String(date.getFullYear()).slice(-2),
        MMMM: MONTHS_LONG[date.getMonth()],
        MMM: MONTHS_SHORT[date.getMonth()],
        MM: pad(date.getMonth() + 1),
        dd: pad(date.getDate()),
        HH: pad(date.getHours()),
        mm: pad(date.getMinutes()),
    };

    return pattern.replace(/yyyy|yy|MMMM|MMM|MM|dd|HH|mm/g, token => tokens[token]);
};

export const formatTime = (value) => {
    const date = toDate(value);
    return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : '';
};

// Accepts "9", "9:5", "930", "0930", "09.30", "9h30" — returns minutes since
// midnight, or null when the input isn't a time. Separator and bare-digit forms
// are handled separately: a single regex makes "930" ambiguous with "93:0".
export const parseTimeInput = (input) => {
    const trimmed = String(input ?? '').trim();
    if (!trimmed) return null;

    let hours;
    let minutes;
    const separated = trimmed.match(/^(\d{1,2})\s*[:.h]\s*(\d{1,2})$/i);

    if (separated) {
        [, hours, minutes] = separated;
    } else if (/^\d{1,4}$/.test(trimmed)) {
        // 1-2 digits are an hour; 3-4 digits split as H|MM and HH|MM.
        const digits = trimmed.length <= 2 ? [trimmed, '0'] : [trimmed.slice(0, -2), trimmed.slice(-2)];
        [hours, minutes] = digits;
    } else {
        return null;
    }

    hours = Number(hours);
    minutes = Number(minutes);
    if (hours > 23 || minutes > 59) return null;

    return hours * 60 + minutes;
};

export const formatDate = (date) => date ? new Date(date).toLocaleDateString() : '';
export const getFiterDatesLabel = (filterDates, label = 'Dates') => {
    const { from, to } = filterDates;

    if (from && to) {
        return `${formatDate(from)} - ${formatDate(to)}`;
    } else if (from) {
        return `${formatDate(from)} - today`;
    } else if (to) {
        return `Until ${formatDate(to)}`;
    }

    return label;
};

export const formatRegions = (countries) => {
    if (!countries?.length) return 'No regions';
    if (countries.includes('worldwide')) return 'Worldwide';
    if (countries.length <= 2) return countries.join(', ').toUpperCase();
    return `${countries.slice(0, 2).join(', ').toUpperCase()} +${countries.length - 2}`;
};

export const getOrderExpiry = (order) => {
    if (!order) return null;
    const screening = order.bacCorrection?.screeningDateCorrection || order.screeningDate;
    if (!screening) return null;
    const period = order.bacCorrection?.defaultHirePeriodCorrection || order.version?.defaultHirePeriod;
    const periodType = order.bacCorrection?.hirePeriodTypeCorrection || order.version?.hirePeriodType;
    if (!period) return null;
    const end = new Date(screening);
    if (periodType === 'hours') end.setHours(end.getHours() + period);
    else end.setDate(end.getDate() + period);
    return end;
};

export const calcTZOffset = (timeZone) => {
    const now = new Date();
    const dateInTimezone1 = new Date(
        now.toLocaleString('en-US', { timeZone })
    );
    const dateInTimezone2 = new Date(
        now.toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
    );

    return (dateInTimezone1 - dateInTimezone2) / (1000 * 60);
};